import type { UnmatchedPatientUpload } from "@elation/api-client/src/stitch-web/models";

import { DocumentEntity } from "@src/features/documents";

import { CareProtocolOpportunityListSkeleton } from "./CareProtocolOpportunityListSkeleton";
import { NeedsReviewPatientUploadCard } from "./NeedsReviewPatientUploadCard";
import { UploadHistoryTable } from "./UploadHistoryTable";

export interface NeedsReviewPatientUploadsListProps {
  documents: UnmatchedPatientUpload[];
  emptyMessage?: string;
  isError: boolean;
  isLoading: boolean;
  searchTerm?: string;
}

export function NeedsReviewPatientUploadsList({
  documents,
  emptyMessage = "No documents need review.",
  isError,
  isLoading,
  searchTerm = "",
}: NeedsReviewPatientUploadsListProps) {
  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const visibleDocuments = documents.filter((item) => {
    if (!normalizedSearchTerm) return true;
    return [
      item.document.fileName,
      item.document.mimeType,
      item.reason,
      DocumentEntity.needsReviewIdentifierSummaryLabel(item.identifierSummary),
      JSON.stringify(item.identifierSummary),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(normalizedSearchTerm);
  });

  if (isLoading) return <CareProtocolOpportunityListSkeleton />;

  if (isError) {
    return (
      <div className="rounded-[12px] border border-border bg-background px-4 py-5 text-[13px] text-muted-foreground">
        Unable to load documents that need review.
      </div>
    );
  }

  if (visibleDocuments.length === 0) {
    return (
      <div className="rounded-[12px] border border-border bg-background px-4 py-5 text-[13px] text-muted-foreground">
        {emptyMessage}
      </div>
    );
  }

  return (
    <>
      <UploadHistoryTable label="Needs review patient documents">
        {visibleDocuments.map((item) => (
          <NeedsReviewPatientUploadCard key={item.document.id} item={item} />
        ))}
      </UploadHistoryTable>
    </>
  );
}
